import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload, IconEdit, IconTrash } from '../components/icons';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

interface InvestmentRecord {
  id: number;
  code: string | null;
  name: string;
  asset_class: string;
  amount: number;
  currency_code: string;
  acquisition_date: string;
  maturity_date: string | null;
  expected_return_rate: number | null;
  status: 'active' | 'vacant' | 'matured' | 'demolished' | 'closed' | 'disposed';
  notes: string | null;
  counterparty: { id: number; name: string; role: string } | null;
  created_by: number | null;
  approved_by: number | null;
  arr: number | null;
  security_description: string | null;
  security_value: number | null;
  lease_start_date?: string | null;
  lease_end_date?: string | null;
  rent_review_date?: string | null;
  insurance_expiry_date?: string | null;
  outstanding_principal?: number;
  coverage_ratio?: number | null;
  npl?: boolean;
  effective_yield?: number | null;
  ytm?: number | null;
  duration?: number | null;
  amortised_cost?: number | null;
  gross_rental_yield?: number | null;
  bank_name?: string | null;
  accrued_interest_to_date?: number;
  rolled_from?: { id: number; code: string | null; name: string } | null;
  rolled_into?: { id: number; code: string | null; name: string } | null;
  units_held?: number | null;
  nav_per_unit?: number | null;
  unrealised_gain_loss?: number | null;
  dividend_yield?: number | null;
  total_return?: number | null;
  country?: string | null;
  fx_movement_since_acquisition?: number | null;
  fx_gain_loss?: number | null;
  is_related_party?: boolean;
  covenant_description?: string | null;
  covenant_breached_at?: string | null;
  restructured_at?: string | null;
  restructure_notes?: string | null;
  written_off_at?: string | null;
  written_off_amount?: number | null;
  write_off_reason?: string | null;
}

interface CorporateAction {
  id: number;
  action_type: string;
  action_date: string;
  units_delta: string | null;
  cash_amount: string | null;
  description: string | null;
}

interface LoanScheduleEntry {
  id: number;
  installment_number: number;
  due_date: string;
  opening_balance: string;
  interest_due: string;
  principal_due: string;
  total_due: string;
  interest_paid: string;
  principal_paid: string;
  paid_date: string | null;
  status: 'pending' | 'partially_paid' | 'paid';
}

interface Valuation {
  id: number;
  period_end: string;
  carrying_value: string;
  market_value: string | null;
  translated_vuv_value: string | null;
}

interface IncomeEntry {
  id: number;
  income_date: string;
  type: string;
  expected_amount: string | null;
  actual_amount: string | null;
  status: string;
  declared_date?: string | null;
  ex_date?: string | null;
  record_date?: string | null;
  repatriated_at?: string | null;
  repatriated_amount?: string | null;
}

interface InvestmentDocument {
  id: number;
  category: string;
  original_name: string;
  date_on_file: string | null;
}

const STATUS_LABELS: Record<InvestmentRecord['status'], string> = {
  active: 'Active',
  vacant: 'Vacant',
  matured: 'Matured',
  demolished: 'Demolished',
  closed: 'Closed',
  disposed: 'Disposed',
};

const STATUS_BADGE: Record<InvestmentRecord['status'], string> = {
  active: 'badge-active',
  vacant: 'badge-scheduled',
  matured: 'badge-neutral',
  demolished: 'badge-neutral',
  closed: 'badge-neutral',
  disposed: 'badge-neutral',
};

export function InvestmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { lists, settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError, showSuccess } = useToast();
  const [investment, setInvestment] = useState<InvestmentRecord | null>(null);
  const [valuations, setValuations] = useState<Valuation[]>([]);
  const [incomeEntries, setIncomeEntries] = useState<IncomeEntry[]>([]);
  const [documents, setDocuments] = useState<InvestmentDocument[]>([]);
  const [loanSchedule, setLoanSchedule] = useState<LoanScheduleEntry[]>([]);
  const [payingEntryId, setPayingEntryId] = useState<number | null>(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', paid_date: '' });

  const [showValuationForm, setShowValuationForm] = useState(false);
  const [valuationForm, setValuationForm] = useState({ period_end: '', carrying_value: '', market_value: '' });
  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [incomeForm, setIncomeForm] = useState({
    income_date: '',
    type: '',
    expected_amount: '',
    actual_amount: '',
    declared_date: '',
    ex_date: '',
    record_date: '',
  });
  const [uploadCategory, setUploadCategory] = useState('');
  const [showRolloverForm, setShowRolloverForm] = useState(false);
  const [rolloverForm, setRolloverForm] = useState({ amount: '', expected_return_rate: '', maturity_date: '' });
  const [corporateActions, setCorporateActions] = useState<CorporateAction[]>([]);
  const [showActionForm, setShowActionForm] = useState(false);
  const [actionForm, setActionForm] = useState({ action_type: '', action_date: '', units_delta: '', cash_amount: '', description: '' });
  const [showWriteOffForm, setShowWriteOffForm] = useState(false);
  const [writeOffForm, setWriteOffForm] = useState({ written_off_amount: '', write_off_reason: '' });
  const [showRestructureForm, setShowRestructureForm] = useState(false);
  const [restructureForm, setRestructureForm] = useState({
    expected_return_rate: '',
    maturity_date: '',
    repayment_frequency: '',
    restructure_notes: '',
  });

  const canUpdate = can('investments.update');
  const canArchive = can('investments.archive');
  const canApprove = can('investments.approve');

  const load = () => api.get<InvestmentRecord>(`/investments/${id}`).then((res) => setInvestment(res.data));
  const loadValuations = () => api.get<Valuation[]>(`/investments/${id}/valuations`).then((res) => setValuations(res.data));
  const loadIncome = () => api.get<IncomeEntry[]>(`/investments/${id}/income-entries`).then((res) => setIncomeEntries(res.data));
  const loadDocuments = () => api.get<InvestmentDocument[]>(`/investments/${id}/documents`).then((res) => setDocuments(res.data));
  const loadLoanSchedule = () => api.get<LoanScheduleEntry[]>(`/investments/${id}/loan-schedule`).then((res) => setLoanSchedule(res.data));
  const loadCorporateActions = () =>
    api.get<CorporateAction[]>(`/investments/${id}/corporate-actions`).then((res) => setCorporateActions(res.data));

  useEffect(() => {
    load();
    loadValuations();
    loadIncome();
    loadDocuments();
    loadLoanSchedule();
    loadCorporateActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fmtMoney = (n: number, currency?: string) => formatMoney(n, currency ?? settings.currency);

  const remove = async () => {
    if (!investment) return;
    if (!(await confirmAction(`Delete "${investment.name}"?`))) return;
    try {
      await api.delete(`/investments/${id}`);
      navigate('/investments');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const dispose = async () => {
    if (!investment) return;
    if (!(await confirmAction('Dispose of this investment? A different Investment Administrator from whoever created it must approve.', { confirmLabel: 'Dispose' }))) return;
    try {
      await api.post(`/investments/${id}/dispose`);
      showSuccess('Investment disposed.');
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Dispose failed.');
    }
  };

  const submitValuation = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/investments/${id}/valuations`, {
        ...valuationForm,
        market_value: valuationForm.market_value || undefined,
      });
      setShowValuationForm(false);
      setValuationForm({ period_end: '', carrying_value: '', market_value: '' });
      loadValuations();
      // A new valuation feeds several per-asset-class figures on the
      // summary/detail-grid above (NAV per unit, unrealised G/L, gross
      // rental yield, dividend yield, total return) — refresh those too.
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to post valuation.');
    }
  };

  const submitIncome = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/investments/${id}/income-entries`, {
        ...incomeForm,
        expected_amount: incomeForm.expected_amount || undefined,
        actual_amount: incomeForm.actual_amount || undefined,
        declared_date: incomeForm.declared_date || undefined,
        ex_date: incomeForm.ex_date || undefined,
        record_date: incomeForm.record_date || undefined,
      });
      setShowIncomeForm(false);
      setIncomeForm({ income_date: '', type: '', expected_amount: '', actual_amount: '', declared_date: '', ex_date: '', record_date: '' });
      loadIncome();
      load(); // refreshes ARR/yield figures derived from income entries
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to record income.');
    }
  };

  const markReceived = async (entry: IncomeEntry) => {
    try {
      await api.put(`/investments/${id}/income-entries/${entry.id}`, {
        income_date: entry.income_date,
        type: entry.type,
        expected_amount: entry.expected_amount || undefined,
        actual_amount: entry.expected_amount || undefined,
      });
      loadIncome();
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to update.');
    }
  };

  const generateInvoice = async (entry: IncomeEntry) => {
    try {
      const res = await api.post<{ id: number }>(`/investments/${id}/income-entries/${entry.id}/generate-invoice`);
      showSuccess('Lease invoice generated.');
      navigate(`/revenue/invoices/${res.data.id}`);
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to generate the invoice.');
    }
  };

  const markRepatriated = async (entry: IncomeEntry) => {
    try {
      await api.put(`/investments/${id}/income-entries/${entry.id}`, {
        income_date: entry.income_date,
        type: entry.type,
        expected_amount: entry.expected_amount || undefined,
        actual_amount: entry.actual_amount || undefined,
        repatriated_at: new Date().toISOString().slice(0, 10),
        repatriated_amount: entry.actual_amount || undefined,
      });
      loadIncome();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to update.');
    }
  };

  const uploadDocument = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fileInput = form.elements.namedItem('file') as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file || !uploadCategory) return;

    const body = new FormData();
    body.append('category', uploadCategory);
    body.append('file', file);
    try {
      await api.post(`/investments/${id}/documents`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadCategory('');
      form.reset();
      loadDocuments();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Upload failed.');
    }
  };

  const generateLoanSchedule = async () => {
    if (
      !(await confirmAction(
        'Generate a repayment schedule from the amount, expected return rate and repayment frequency? Any existing pending installments will be replaced.',
        { confirmLabel: 'Generate' }
      ))
    )
      return;
    try {
      await api.post(`/investments/${id}/loan-schedule/generate`);
      loadLoanSchedule();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to generate schedule.');
    }
  };

  const generateIncomeSchedule = async () => {
    const prompt =
      investment?.asset_class === 'property'
        ? 'Generate the rent schedule from the property amount, expected return rate, lease dates and rent frequency?'
        : investment?.asset_class === 'cash'
          ? 'Generate the maturity interest entry from the deposit amount, expected return rate and maturity date?'
          : 'Generate the coupon schedule from face value, coupon rate, maturity date and coupon frequency?';
    if (!(await confirmAction(prompt, { confirmLabel: 'Generate' }))) return;
    try {
      await api.post(`/investments/${id}/income-schedule/generate`);
      loadIncome();
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to generate schedule.');
    }
  };

  const submitRollover = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const res = await api.post<{ id: number }>(`/investments/${id}/rollover`, rolloverForm);
      setShowRolloverForm(false);
      setRolloverForm({ amount: '', expected_return_rate: '', maturity_date: '' });
      load();
      showSuccess('Rolled over. Redirecting to the new investment…');
      navigate(`/investments/${res.data.id}`);
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to roll over.');
    }
  };

  const submitWriteOff = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/investments/${id}/write-off`, writeOffForm);
      setShowWriteOffForm(false);
      setWriteOffForm({ written_off_amount: '', write_off_reason: '' });
      showSuccess('Loan written off.');
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to write off.');
    }
  };

  const submitRestructure = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/investments/${id}/restructure`, {
        ...restructureForm,
        expected_return_rate: restructureForm.expected_return_rate || undefined,
        maturity_date: restructureForm.maturity_date || undefined,
        repayment_frequency: restructureForm.repayment_frequency || undefined,
      });
      setShowRestructureForm(false);
      setRestructureForm({ expected_return_rate: '', maturity_date: '', repayment_frequency: '', restructure_notes: '' });
      showSuccess('Loan restructured.');
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to restructure.');
    }
  };

  const flagCovenantBreach = async () => {
    if (!(await confirmAction('Flag this loan\'s covenant as breached as of today?', { confirmLabel: 'Flag Breach' }))) return;
    try {
      await api.post(`/investments/${id}/covenant-breach`, {});
      showSuccess('Covenant breach flagged.');
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to flag covenant breach.');
    }
  };

  const submitCorporateAction = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/investments/${id}/corporate-actions`, {
        ...actionForm,
        units_delta: actionForm.units_delta || undefined,
        cash_amount: actionForm.cash_amount || undefined,
        description: actionForm.description || undefined,
      });
      setShowActionForm(false);
      setActionForm({ action_type: '', action_date: '', units_delta: '', cash_amount: '', description: '' });
      loadCorporateActions();
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to log corporate action.');
    }
  };

  const removeCorporateAction = async (action: CorporateAction) => {
    if (!(await confirmAction('Remove this corporate action? Its units_delta will be reversed from units held.'))) return;
    try {
      await api.delete(`/investments/${id}/corporate-actions/${action.id}`);
      loadCorporateActions();
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to delete.');
    }
  };

  const submitPayment = async (e: FormEvent, entryId: number) => {
    e.preventDefault();
    try {
      await api.post(`/investments/${id}/loan-schedule/${entryId}/record-payment`, paymentForm);
      setPayingEntryId(null);
      setPaymentForm({ amount: '', paid_date: '' });
      loadLoanSchedule();
      loadIncome();
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to record payment.');
    }
  };

  const removeDocument = async (doc: InvestmentDocument) => {
    if (!(await confirmAction(`Delete "${doc.original_name}"?`))) return;
    try {
      await api.delete(`/investment-documents/${doc.id}`);
      loadDocuments();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!investment) return null;

  const isOverdue = (entry: IncomeEntry) => !entry.actual_amount && new Date(entry.income_date) < new Date();

  // Days-until badge for a lease/insurance expiry date — expired or within
  // 90 days gets flagged, matching the tender's lease-expiry-alert ask.
  const expiryBadge = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    const days = Math.floor((new Date(dateStr).getTime() - Date.now()) / 86400000);
    if (days < 0) return <span className="badge badge-urgent">Expired</span>;
    if (days <= 90) return <span className="badge badge-scheduled">Expires in {days}d</span>;
    return null;
  };

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/investments')} />
          {canUpdate && <IconButton icon={IconEdit} label="Edit" to={`/investments/${id}/edit`} />}
          {canApprove && investment.status !== 'disposed' && (
            <button type="button" className="btn btn-danger-ghost" onClick={dispose}>
              Dispose
            </button>
          )}
          {canArchive && <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={remove} />}
          {canUpdate &&
            investment.asset_class === 'cash' &&
            investment.status === 'active' &&
            investment.maturity_date &&
            new Date(investment.maturity_date) <= new Date() && (
              <button type="button" className="btn btn-ghost" onClick={() => setShowRolloverForm((v) => !v)}>
                Roll Over
              </button>
            )}
          {canApprove && investment.asset_class === 'loans' && !investment.written_off_at && (
            <button type="button" className="btn btn-danger-ghost" onClick={() => setShowWriteOffForm((v) => !v)}>
              Write Off
            </button>
          )}
          {canApprove && investment.asset_class === 'loans' && (
            <button type="button" className="btn btn-ghost" onClick={() => setShowRestructureForm((v) => !v)}>
              Restructure
            </button>
          )}
          {canUpdate && investment.asset_class === 'loans' && !investment.covenant_breached_at && (
            <button type="button" className="btn btn-ghost" onClick={flagCovenantBreach}>
              Flag Covenant Breach
            </button>
          )}
        </div>
      </header>

      {showRolloverForm && (
        <div className="card">
          <h2>Roll Over Deposit</h2>
          <form onSubmit={submitRollover} className="form-grid">
            <label>
              New amount *
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={rolloverForm.amount}
                onChange={(e) => setRolloverForm({ ...rolloverForm, amount: e.target.value })}
              />
            </label>
            <label>
              New expected return (%)
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={rolloverForm.expected_return_rate}
                onChange={(e) => setRolloverForm({ ...rolloverForm, expected_return_rate: e.target.value })}
              />
            </label>
            <label>
              New maturity date *
              <input
                type="date"
                required
                value={rolloverForm.maturity_date}
                onChange={(e) => setRolloverForm({ ...rolloverForm, maturity_date: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button className="btn btn-primary">Roll Over</button>
            </div>
          </form>
        </div>
      )}

      {showWriteOffForm && (
        <div className="card">
          <h2>Write Off Loan</h2>
          <form onSubmit={submitWriteOff} className="form-grid">
            <label>
              Amount written off *
              <input
                type="number"
                step="0.01"
                min="0.01"
                required
                value={writeOffForm.written_off_amount}
                onChange={(e) => setWriteOffForm({ ...writeOffForm, written_off_amount: e.target.value })}
              />
            </label>
            <label className="span-2">
              Reason *
              <textarea
                required
                rows={2}
                value={writeOffForm.write_off_reason}
                onChange={(e) => setWriteOffForm({ ...writeOffForm, write_off_reason: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setShowWriteOffForm(false)}>
                Cancel
              </button>
              <button className="btn btn-danger">Write Off</button>
            </div>
          </form>
        </div>
      )}

      {showRestructureForm && (
        <div className="card">
          <h2>Restructure Loan</h2>
          <form onSubmit={submitRestructure} className="form-grid">
            <label>
              New expected return (%)
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={restructureForm.expected_return_rate}
                onChange={(e) => setRestructureForm({ ...restructureForm, expected_return_rate: e.target.value })}
              />
            </label>
            <label>
              New maturity date
              <input
                type="date"
                value={restructureForm.maturity_date}
                onChange={(e) => setRestructureForm({ ...restructureForm, maturity_date: e.target.value })}
              />
            </label>
            <label>
              New repayment frequency
              <select
                value={restructureForm.repayment_frequency}
                onChange={(e) => setRestructureForm({ ...restructureForm, repayment_frequency: e.target.value })}
              >
                <option value="">Unchanged</option>
                {listOptions(lists, 'investment_repayment_frequency').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="span-2">
              Notes *
              <textarea
                required
                rows={2}
                value={restructureForm.restructure_notes}
                onChange={(e) => setRestructureForm({ ...restructureForm, restructure_notes: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setShowRestructureForm(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Restructure</button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{investment.name}</div>
            <span className="muted">{investment.code}</span>{' '}
            <span className={`badge ${STATUS_BADGE[investment.status]}`}>{STATUS_LABELS[investment.status]}</span>
            <div className="detail-summary-service">{labelFor(lists, 'investment_asset_class', investment.asset_class)}</div>
            {investment.npl && <span className="badge badge-urgent">NPL — 90+ days overdue</span>}
            {investment.written_off_at && <span className="badge badge-urgent">Written Off {investment.written_off_at}</span>}
            {investment.covenant_breached_at && (
              <span className="badge badge-urgent">Covenant Breached {investment.covenant_breached_at}</span>
            )}
            {investment.is_related_party && <span className="badge badge-scheduled">Related Party</span>}
          </div>
          {investment.asset_class === 'loans' ? (
            investment.effective_yield !== null && investment.effective_yield !== undefined && (
              <div>
                <div className="detail-item-label">Effective Yield</div>
                <div className="detail-summary-amount">{(investment.effective_yield * 100).toFixed(2)}%</div>
              </div>
            )
          ) : investment.asset_class === 'fixed_interest' ? (
            investment.ytm !== null && investment.ytm !== undefined && (
              <div>
                <div className="detail-item-label">Yield to Maturity</div>
                <div className="detail-summary-amount">{(investment.ytm * 100).toFixed(2)}%</div>
              </div>
            )
          ) : investment.asset_class === 'equity' ? (
            investment.total_return !== null && investment.total_return !== undefined && (
              <div>
                <div className="detail-item-label">Total Return</div>
                <div className="detail-summary-amount">{(investment.total_return * 100).toFixed(2)}%</div>
              </div>
            )
          ) : (
            investment.arr !== null && (
              <div>
                <div className="detail-item-label">Annualised Return (ARR)</div>
                <div className="detail-summary-amount">{(investment.arr * 100).toFixed(2)}%</div>
              </div>
            )
          )}
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Amount</span>
            <span className="detail-item-value">{fmtMoney(investment.amount, investment.currency_code)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Expected return</span>
            <span className={`detail-item-value${investment.expected_return_rate != null ? '' : ' empty'}`}>
              {investment.expected_return_rate != null ? `${investment.expected_return_rate}%` : 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Acquisition date</span>
            <span className="detail-item-value">{investment.acquisition_date}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Maturity date</span>
            <span className={`detail-item-value${investment.maturity_date ? '' : ' empty'}`}>
              {investment.maturity_date || 'Not set'}
            </span>
          </div>
          {investment.counterparty && (
            <div className="detail-item">
              <span className="detail-item-label">{investment.counterparty.role === 'tenant' ? 'Tenant' : 'Borrower'}</span>
              <span className="detail-item-value">{investment.counterparty.name}</span>
            </div>
          )}
          {investment.asset_class === 'loans' && (
            <>
              <div className="detail-item">
                <span className="detail-item-label">Outstanding Principal</span>
                <span className="detail-item-value">
                  {investment.outstanding_principal !== undefined ? fmtMoney(investment.outstanding_principal, investment.currency_code) : '—'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Coverage Ratio</span>
                <span className={`detail-item-value${investment.coverage_ratio != null ? '' : ' empty'}`}>
                  {investment.coverage_ratio != null ? `${investment.coverage_ratio.toFixed(2)}×` : 'No security value set'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Security</span>
                <span className={`detail-item-value${investment.security_value != null ? '' : ' empty'}`}>
                  {investment.security_value != null ? fmtMoney(investment.security_value, investment.currency_code) : 'Not set'}
                </span>
              </div>
              {investment.covenant_description && (
                <div className="detail-item">
                  <span className="detail-item-label">Covenant</span>
                  <span className="detail-item-value">{investment.covenant_description}</span>
                </div>
              )}
              {investment.written_off_at && (
                <div className="detail-item">
                  <span className="detail-item-label">Write-Off Reason</span>
                  <span className="detail-item-value">
                    {fmtMoney(investment.written_off_amount ?? 0, investment.currency_code)} — {investment.write_off_reason}
                  </span>
                </div>
              )}
              {investment.restructured_at && (
                <div className="detail-item">
                  <span className="detail-item-label">Last Restructured {investment.restructured_at}</span>
                  <span className="detail-item-value">{investment.restructure_notes}</span>
                </div>
              )}
            </>
          )}
          {investment.asset_class === 'fixed_interest' && (
            <>
              <div className="detail-item">
                <span className="detail-item-label">Macaulay Duration</span>
                <span className={`detail-item-value${investment.duration != null ? '' : ' empty'}`}>
                  {investment.duration != null ? `${investment.duration.toFixed(2)} yrs` : 'Generate a coupon schedule first'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Amortised Cost</span>
                <span className={`detail-item-value${investment.amortised_cost != null ? '' : ' empty'}`}>
                  {investment.amortised_cost != null ? fmtMoney(investment.amortised_cost, investment.currency_code) : 'Generate a coupon schedule first'}
                </span>
              </div>
            </>
          )}
          {investment.asset_class === 'property' && (
            <>
              <div className="detail-item">
                <span className="detail-item-label">Lease start</span>
                <span className={`detail-item-value${investment.lease_start_date ? '' : ' empty'}`}>
                  {investment.lease_start_date || 'Not set'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Lease end</span>
                <span className={`detail-item-value${investment.lease_end_date ? '' : ' empty'}`}>
                  {investment.lease_end_date || 'Not set'} {expiryBadge(investment.lease_end_date)}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Rent review date</span>
                <span className={`detail-item-value${investment.rent_review_date ? '' : ' empty'}`}>
                  {investment.rent_review_date || 'Not set'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Insurance expiry</span>
                <span className={`detail-item-value${investment.insurance_expiry_date ? '' : ' empty'}`}>
                  {investment.insurance_expiry_date || 'Not set'} {expiryBadge(investment.insurance_expiry_date)}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Gross Rental Yield</span>
                <span className={`detail-item-value${investment.gross_rental_yield != null ? '' : ' empty'}`}>
                  {investment.gross_rental_yield != null ? `${(investment.gross_rental_yield * 100).toFixed(2)}%` : 'No rent received yet'}
                </span>
              </div>
            </>
          )}
          {investment.asset_class === 'cash' && (
            <>
              <div className="detail-item">
                <span className="detail-item-label">Bank</span>
                <span className={`detail-item-value${investment.bank_name ? '' : ' empty'}`}>{investment.bank_name || 'Not set'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Accrued Interest to Date</span>
                <span className="detail-item-value">
                  {investment.accrued_interest_to_date !== undefined
                    ? fmtMoney(investment.accrued_interest_to_date, investment.currency_code)
                    : '—'}
                </span>
              </div>
              {investment.rolled_from && (
                <div className="detail-item">
                  <span className="detail-item-label">Rolled from</span>
                  <span className="detail-item-value">
                    <Link to={`/investments/${investment.rolled_from.id}`}>
                      {investment.rolled_from.code} — {investment.rolled_from.name}
                    </Link>
                  </span>
                </div>
              )}
              {investment.rolled_into && (
                <div className="detail-item">
                  <span className="detail-item-label">Rolled into</span>
                  <span className="detail-item-value">
                    <Link to={`/investments/${investment.rolled_into.id}`}>
                      {investment.rolled_into.code} — {investment.rolled_into.name}
                    </Link>
                  </span>
                </div>
              )}
            </>
          )}
          {investment.asset_class === 'equity' && (
            <>
              <div className="detail-item">
                <span className="detail-item-label">Units held</span>
                <span className={`detail-item-value${investment.units_held != null ? '' : ' empty'}`}>
                  {investment.units_held != null ? investment.units_held : 'Not set'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">NAV per unit</span>
                <span className={`detail-item-value${investment.nav_per_unit != null ? '' : ' empty'}`}>
                  {investment.nav_per_unit != null ? fmtMoney(investment.nav_per_unit, investment.currency_code) : 'No valuation/units set'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Unrealised Gain/Loss</span>
                <span className={`detail-item-value${investment.unrealised_gain_loss != null ? '' : ' empty'}`}>
                  {investment.unrealised_gain_loss != null ? fmtMoney(investment.unrealised_gain_loss, investment.currency_code) : 'No valuation posted yet'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Dividend Yield</span>
                <span className={`detail-item-value${investment.dividend_yield != null ? '' : ' empty'}`}>
                  {investment.dividend_yield != null ? `${(investment.dividend_yield * 100).toFixed(2)}%` : 'No dividends received yet'}
                </span>
              </div>
            </>
          )}
          {investment.asset_class === 'offshore' && (
            <>
              <div className="detail-item">
                <span className="detail-item-label">Country</span>
                <span className={`detail-item-value${investment.country ? '' : ' empty'}`}>{investment.country || 'Not set'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Currency</span>
                <span className="detail-item-value">{investment.currency_code}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">FX Movement Since Acquisition</span>
                <span className={`detail-item-value${investment.fx_movement_since_acquisition != null ? '' : ' empty'}`}>
                  {investment.fx_movement_since_acquisition != null
                    ? `${(investment.fx_movement_since_acquisition * 100).toFixed(2)}%`
                    : 'Needs 2+ valuation snapshots'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">FX Gain/Loss (VUV)</span>
                <span className={`detail-item-value${investment.fx_gain_loss != null ? '' : ' empty'}`}>
                  {investment.fx_gain_loss != null ? fmtMoney(investment.fx_gain_loss, 'VUV') : 'Needs 2+ valuation snapshots'}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Notes</span>
          {investment.notes ? (
            <p className="detail-notes">{investment.notes}</p>
          ) : (
            <span className="detail-item-value empty">Not provided</span>
          )}
        </div>
      </div>

      {investment.asset_class === 'loans' && (
        <div className="card">
          <div className="changelog-release-header">
            <h2>Repayment Schedule</h2>
            {canUpdate && loanSchedule.every((e) => e.status === 'pending') && (
              <button type="button" className="btn btn-ghost" onClick={generateLoanSchedule}>
                {loanSchedule.length > 0 ? 'Regenerate Schedule' : 'Generate Schedule'}
              </button>
            )}
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Due Date</th>
                  <th className="right">Opening Balance</th>
                  <th className="right">Interest</th>
                  <th className="right">Principal</th>
                  <th className="right">Total Due</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loanSchedule.length > 0 ? (
                  loanSchedule.flatMap((entry) => {
                    const overdue = entry.status !== 'paid' && new Date(entry.due_date) < new Date();
                    const rows = [
                        <tr key={entry.id}>
                          <td>{entry.installment_number}</td>
                          <td>{entry.due_date}</td>
                          <td className="right">{fmtMoney(Number(entry.opening_balance), investment.currency_code)}</td>
                          <td className="right">{fmtMoney(Number(entry.interest_due), investment.currency_code)}</td>
                          <td className="right">{fmtMoney(Number(entry.principal_due), investment.currency_code)}</td>
                          <td className="right">{fmtMoney(Number(entry.total_due), investment.currency_code)}</td>
                          <td>
                            <span
                              className={`badge ${overdue ? 'badge-urgent' : entry.status === 'paid' ? 'badge-active' : entry.status === 'partially_paid' ? 'badge-scheduled' : 'badge-neutral'}`}
                            >
                              {overdue ? 'Overdue' : entry.status === 'partially_paid' ? 'Partially Paid' : entry.status === 'paid' ? 'Paid' : 'Pending'}
                            </span>
                          </td>
                          <td className="right">
                            {canUpdate && entry.status !== 'paid' && (
                              <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => {
                                  setPayingEntryId(payingEntryId === entry.id ? null : entry.id);
                                  setPaymentForm({ amount: entry.total_due, paid_date: '' });
                                }}
                              >
                                Record Payment
                              </button>
                            )}
                          </td>
                        </tr>,
                    ];
                    if (payingEntryId === entry.id) {
                      rows.push(
                        <tr key={`${entry.id}-form`}>
                          <td colSpan={8}>
                            <form onSubmit={(e) => submitPayment(e, entry.id)} className="form-grid">
                              <label>
                                Amount *
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  required
                                  value={paymentForm.amount}
                                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                                />
                              </label>
                              <label>
                                Paid date *
                                <input
                                  type="date"
                                  required
                                  value={paymentForm.paid_date}
                                  onChange={(e) => setPaymentForm({ ...paymentForm, paid_date: e.target.value })}
                                />
                              </label>
                              <div className="form-actions">
                                <button className="btn btn-primary">Save Payment</button>
                              </div>
                            </form>
                          </td>
                        </tr>
                      );
                    }
                    return rows;
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="muted center">
                      No schedule generated yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {investment.asset_class === 'equity' && (
        <div className="card">
          <div className="changelog-release-header">
            <h2>Corporate Actions</h2>
            {canUpdate && (
              <button type="button" className="btn btn-ghost" onClick={() => setShowActionForm((v) => !v)}>
                + Add Action
              </button>
            )}
          </div>
          {showActionForm && (
            <form onSubmit={submitCorporateAction} className="form-grid">
              <label>
                Type *
                <select
                  required
                  value={actionForm.action_type}
                  onChange={(e) => setActionForm({ ...actionForm, action_type: e.target.value })}
                >
                  <option value="">Select…</option>
                  {listOptions(lists, 'investment_corporate_action_type').map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Date *
                <input
                  type="date"
                  required
                  value={actionForm.action_date}
                  onChange={(e) => setActionForm({ ...actionForm, action_date: e.target.value })}
                />
              </label>
              <label>
                Units delta
                <input
                  type="number"
                  step="0.0001"
                  value={actionForm.units_delta}
                  onChange={(e) => setActionForm({ ...actionForm, units_delta: e.target.value })}
                />
              </label>
              <label>
                Cash amount
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={actionForm.cash_amount}
                  onChange={(e) => setActionForm({ ...actionForm, cash_amount: e.target.value })}
                />
              </label>
              <label className="span-2">
                Description
                <textarea rows={2} value={actionForm.description} onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })} />
              </label>
              <div className="form-actions span-2">
                <button className="btn btn-primary">Save</button>
              </div>
            </form>
          )}
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th className="right">Units Δ</th>
                  <th className="right">Cash</th>
                  <th>Description</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {corporateActions.length > 0 ? (
                  corporateActions.map((action) => (
                    <tr key={action.id}>
                      <td>{action.action_date}</td>
                      <td>{labelFor(lists, 'investment_corporate_action_type', action.action_type)}</td>
                      <td className="right">{action.units_delta ?? '—'}</td>
                      <td className="right">{action.cash_amount ? fmtMoney(Number(action.cash_amount), investment.currency_code) : '—'}</td>
                      <td>{action.description || '—'}</td>
                      <td>
                        {canUpdate && (
                          <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => removeCorporateAction(action)} />
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="muted center">
                      No corporate actions logged yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <div className="changelog-release-header">
          <h2>Valuations</h2>
          {canUpdate && (
            <button type="button" className="btn btn-ghost" onClick={() => setShowValuationForm((v) => !v)}>
              + Add Snapshot
            </button>
          )}
        </div>
        {showValuationForm && (
          <form onSubmit={submitValuation} className="form-grid">
            <label>
              Period end *
              <input
                type="date"
                required
                value={valuationForm.period_end}
                onChange={(e) => setValuationForm({ ...valuationForm, period_end: e.target.value })}
              />
            </label>
            <label>
              Carrying value *
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={valuationForm.carrying_value}
                onChange={(e) => setValuationForm({ ...valuationForm, carrying_value: e.target.value })}
              />
            </label>
            <label>
              Market value
              <input
                type="number"
                step="0.01"
                min="0"
                value={valuationForm.market_value}
                onChange={(e) => setValuationForm({ ...valuationForm, market_value: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        )}
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Period End</th>
                <th className="right">Carrying Value</th>
                <th className="right">Market Value</th>
              </tr>
            </thead>
            <tbody>
              {valuations.length > 0 ? (
                valuations.map((v) => (
                  <tr key={v.id}>
                    <td>{v.period_end}</td>
                    <td className="right">{fmtMoney(Number(v.carrying_value), investment.currency_code)}</td>
                    <td className="right">{v.market_value ? fmtMoney(Number(v.market_value), investment.currency_code) : '—'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="muted center">
                    No valuations posted yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="changelog-release-header">
          <h2>Income</h2>
          <div className="page-header-actions">
            {canUpdate && investment.asset_class === 'fixed_interest' && incomeEntries.length === 0 && (
              <button type="button" className="btn btn-ghost" onClick={generateIncomeSchedule}>
                Generate Coupon Schedule
              </button>
            )}
            {canUpdate && investment.asset_class === 'property' && incomeEntries.length === 0 && (
              <button type="button" className="btn btn-ghost" onClick={generateIncomeSchedule}>
                Generate Rent Schedule
              </button>
            )}
            {canUpdate && investment.asset_class === 'cash' && incomeEntries.length === 0 && (
              <button type="button" className="btn btn-ghost" onClick={generateIncomeSchedule}>
                Generate Interest Entry
              </button>
            )}
            {canUpdate && (
              <button type="button" className="btn btn-ghost" onClick={() => setShowIncomeForm((v) => !v)}>
                + Add Entry
              </button>
            )}
          </div>
        </div>
        {showIncomeForm && (
          <form onSubmit={submitIncome} className="form-grid">
            <label>
              Date *
              <input
                type="date"
                required
                value={incomeForm.income_date}
                onChange={(e) => setIncomeForm({ ...incomeForm, income_date: e.target.value })}
              />
            </label>
            <label>
              Type *
              <select
                required
                value={incomeForm.type}
                onChange={(e) => setIncomeForm({ ...incomeForm, type: e.target.value })}
              >
                <option value="">Select…</option>
                {listOptions(lists, 'investment_income_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Expected amount
              <input
                type="number"
                step="0.01"
                min="0"
                value={incomeForm.expected_amount}
                onChange={(e) => setIncomeForm({ ...incomeForm, expected_amount: e.target.value })}
              />
            </label>
            <label>
              Actual amount (if received)
              <input
                type="number"
                step="0.01"
                min="0"
                value={incomeForm.actual_amount}
                onChange={(e) => setIncomeForm({ ...incomeForm, actual_amount: e.target.value })}
              />
            </label>
            {incomeForm.type === 'dividend' && (
              <>
                <label>
                  Declared date
                  <input
                    type="date"
                    value={incomeForm.declared_date}
                    onChange={(e) => setIncomeForm({ ...incomeForm, declared_date: e.target.value })}
                  />
                </label>
                <label>
                  Ex-dividend date
                  <input
                    type="date"
                    value={incomeForm.ex_date}
                    onChange={(e) => setIncomeForm({ ...incomeForm, ex_date: e.target.value })}
                  />
                </label>
                <label>
                  Record date
                  <input
                    type="date"
                    value={incomeForm.record_date}
                    onChange={(e) => setIncomeForm({ ...incomeForm, record_date: e.target.value })}
                  />
                </label>
              </>
            )}
            <div className="form-actions span-2">
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        )}
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th className="right">Expected</th>
                <th className="right">Actual</th>
                <th>Status</th>
                {investment.asset_class === 'offshore' && <th>Repatriated</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {incomeEntries.length > 0 ? (
                incomeEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.income_date}</td>
                    <td>{labelFor(lists, 'investment_income_type', entry.type)}</td>
                    <td className="right">{entry.expected_amount ? fmtMoney(Number(entry.expected_amount), investment.currency_code) : '—'}</td>
                    <td className="right">{entry.actual_amount ? fmtMoney(Number(entry.actual_amount), investment.currency_code) : '—'}</td>
                    <td>
                      <span className={`badge ${isOverdue(entry) ? 'badge-urgent' : entry.status === 'received' ? 'badge-active' : 'badge-neutral'}`}>
                        {isOverdue(entry) ? 'Overdue' : entry.status === 'received' ? 'Received' : 'Expected'}
                      </span>
                    </td>
                    {investment.asset_class === 'offshore' && (
                      <td>
                        {entry.repatriated_at ? (
                          <span className="badge badge-active">{entry.repatriated_at}</span>
                        ) : entry.actual_amount ? (
                          <span className="badge badge-neutral">Offshore</span>
                        ) : (
                          '—'
                        )}
                      </td>
                    )}
                    <td className="right">
                      {canUpdate && !entry.actual_amount && (
                        <button type="button" className="btn btn-ghost" onClick={() => markReceived(entry)}>
                          Mark Received
                        </button>
                      )}
                      {canUpdate && investment.asset_class === 'offshore' && entry.actual_amount && !entry.repatriated_at && (
                        <button type="button" className="btn btn-ghost" onClick={() => markRepatriated(entry)}>
                          Mark Repatriated
                        </button>
                      )}
                      {canUpdate && investment.asset_class === 'property' && entry.type === 'rent' && !entry.actual_amount && (
                        <button type="button" className="btn btn-ghost" onClick={() => generateInvoice(entry)}>
                          Generate Invoice
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={investment.asset_class === 'offshore' ? 7 : 6} className="muted center">
                    No income recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="changelog-release-header">
          <h2>Documents</h2>
        </div>
        {canUpdate && (
          <form onSubmit={uploadDocument} className="form-grid">
            <label>
              Category *
              <select required value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}>
                <option value="">Select…</option>
                {listOptions(lists, 'investment_document_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="span-2">
              File *
              <input type="file" name="file" required accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
            </label>
            <div className="form-actions">
              <button className="btn btn-primary">Upload</button>
            </div>
          </form>
        )}
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>File</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {documents.length > 0 ? (
                documents.map((doc) => (
                  <tr key={doc.id}>
                    <td>{labelFor(lists, 'investment_document_type', doc.category)}</td>
                    <td>{doc.original_name}</td>
                    <td className="right">
                      <IconButton
                        icon={IconDownload}
                        label="Download"
                        onClick={() => downloadFile(api, `/investment-documents/${doc.id}/download`, doc.original_name)}
                      />
                      {canArchive && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => removeDocument(doc)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="muted center">
                    No documents uploaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
